import React, { useState, useRef } from "react";
import { Plus, X, Camera, Info, Save, Trash2, User, UserCheck, UserMinus } from "lucide-react";

// Types
export interface Pin {
  id: string;
  x: number; // percentage 0-100
  y: number; // percentage 0-100
  type: "botox" | "filler" | "laser" | "surgery" | "other";
  notes: string;
  view: "face" | "body-front" | "body-back";
}

export interface Photo {
  id: string;
  url: string;
  date: string;
  label: "before" | "after" | "progress";
}

interface DermBodyMapProps {
  isRecordLocked?: boolean;
  pins?: Pin[];
  onChange?: (pins: Pin[]) => void;
  photos?: Photo[];
  onPhotosChange?: (photos: Photo[]) => void;
}

const DermBodyMap: React.FC<DermBodyMapProps> = ({ isRecordLocked, pins = [], onChange, photos = [], onPhotosChange }) => {
  const [activePinId, setActivePinId] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<"face" | "body-front" | "body-back">("face");
  
  // To handle new pin creation modal
  const [pendingCoords, setPendingCoords] = useState<{x: number, y: number} | null>(null);
  const [newPinType, setNewPinType] = useState<Pin["type"]>("botox");
  const [newPinNotes, setNewPinNotes] = useState("");
  
  const svgRef = useRef<SVGSVGElement>(null);

  const handleSvgClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (isRecordLocked) return;
    if (activePinId) {
      setActivePinId(null);
      return;
    }
    
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setPendingCoords({ x, y });
  };

  const handleSavePin = () => {
    if (!pendingCoords) return;
    
    const newPin: Pin = {
      id: Math.random().toString(36).substr(2, 9),
      x: pendingCoords.x,
      y: pendingCoords.y,
      type: newPinType,
      notes: newPinNotes,
      view: currentView
    };
    
    const newPins = [...pins, newPin];
    if (onChange) onChange(newPins);
    setPendingCoords(null);
    setNewPinNotes("");
    setNewPinType("botox");
  };
  
  const handleDeletePin = (id: string) => {
    const newPins = pins.filter(p => p.id !== id);
    if (onChange) onChange(newPins);
    setActivePinId(null);
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (ev) => {
        const url = ev.target?.result as string;
        const newPhoto: Photo = {
          id: Math.random().toString(36).substr(2, 9),
          url,
          date: new Date().toLocaleDateString(),
          label: "progress"
        };
        if (onPhotosChange) onPhotosChange([...photos, newPhoto]);
      };
      reader.readAsDataURL(file);
    }
  };

  const getPinColor = (type: Pin["type"]) => {
    switch (type) {
      case "botox": return "bg-purple-500 border-purple-700";
      case "filler": return "bg-pink-500 border-pink-700";
      case "laser": return "bg-orange-500 border-orange-700";
      case "surgery": return "bg-red-500 border-red-700";
      default: return "bg-blue-500 border-blue-700";
    }
  };

  const visiblePins = pins.filter(p => p.view === currentView || (!p.view && currentView === 'face'));

  return (
    <div className="w-full bg-white p-6 rounded-lg shadow-sm border border-gray-200 mt-6">
      <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-2">
        <h2 className="text-sm font-extrabold text-gray-800 flex items-center gap-2">
          <Info className="w-4 h-4 text-indigo-500" />
          Aesthetic & Dermatological Mapping
        </h2>
        
        {/* View Toggles */}
        <div className="flex bg-slate-100 p-1 rounded-lg shadow-inner">
          <button 
            onClick={() => {setCurrentView("face"); setPendingCoords(null); setActivePinId(null);}}
            className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${currentView === 'face' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Face
          </button>
          <button 
            onClick={() => {setCurrentView("body-front"); setPendingCoords(null); setActivePinId(null);}}
            className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${currentView === 'body-front' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Body (Front)
          </button>
          <button 
            onClick={() => {setCurrentView("body-back"); setPendingCoords(null); setActivePinId(null);}}
            className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${currentView === 'body-back' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Body (Back)
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Map Column */}
        <div className="flex flex-col items-center">
          <div className="relative w-[300px] h-[400px] bg-slate-50 border border-slate-200 rounded-xl shadow-inner">
            
            {/* SVG Renderer based on currentView */}
            <svg 
              ref={svgRef}
              className={`w-full h-full cursor-crosshair ${isRecordLocked ? "pointer-events-none opacity-80" : ""}`}
              viewBox="0 0 300 400" 
              onClick={handleSvgClick}
            >
              {currentView === "face" && (
                <>
                  <ellipse cx="150" cy="180" rx="90" ry="120" fill="#fcf8f2" stroke="#d1c4b2" strokeWidth="3" />
                  <path d="M 70 230 Q 150 350 230 230" fill="none" stroke="#d1c4b2" strokeWidth="3" />
                  <path d="M 60 170 C 40 160 40 210 65 210" fill="none" stroke="#d1c4b2" strokeWidth="2" />
                  <path d="M 240 170 C 260 160 260 210 235 210" fill="none" stroke="#d1c4b2" strokeWidth="2" />
                  <ellipse cx="110" cy="160" rx="15" ry="8" fill="none" stroke="#a39684" strokeWidth="2" />
                  <ellipse cx="190" cy="160" rx="15" ry="8" fill="none" stroke="#a39684" strokeWidth="2" />
                  <circle cx="110" cy="160" r="3" fill="#a39684" />
                  <circle cx="190" cy="160" r="3" fill="#a39684" />
                  <path d="M 90 140 Q 110 130 130 140" fill="none" stroke="#a39684" strokeWidth="2" />
                  <path d="M 170 140 Q 190 130 210 140" fill="none" stroke="#a39684" strokeWidth="2" />
                  <path d="M 150 160 L 150 210 Q 140 220 150 220 Q 160 220 150 210" fill="none" stroke="#a39684" strokeWidth="2" />
                  <path d="M 125 245 Q 150 240 175 245 Q 150 255 125 245" fill="none" stroke="#a39684" strokeWidth="2" />
                  <path d="M 125 245 Q 150 260 175 245" fill="none" stroke="#a39684" strokeWidth="2" />
                  <path d="M 100 280 L 90 400 M 200 280 L 210 400" fill="none" stroke="#d1c4b2" strokeWidth="3" />
                </>
              )}

              {currentView === "body-front" && (
                <>
                  <circle cx="150" cy="50" r="25" fill="#fcf8f2" stroke="#d1c4b2" strokeWidth="3" />
                  <rect x="140" y="75" width="20" height="20" fill="#fcf8f2" stroke="#d1c4b2" strokeWidth="3" />
                  <path d="M 100 95 L 200 95 Q 210 160 185 210 L 115 210 Q 90 160 100 95" fill="#fcf8f2" stroke="#d1c4b2" strokeWidth="3" />
                  <path d="M 100 95 Q 70 160 60 210" fill="none" stroke="#fcf8f2" strokeWidth="24" strokeLinecap="round" />
                  <path d="M 100 95 Q 70 160 60 210" fill="none" stroke="#d1c4b2" strokeWidth="24" strokeLinecap="round" />
                  <path d="M 100 95 Q 70 160 60 210" fill="none" stroke="#fcf8f2" strokeWidth="18" strokeLinecap="round" />
                  <path d="M 200 95 Q 230 160 240 210" fill="none" stroke="#d1c4b2" strokeWidth="24" strokeLinecap="round" />
                  <path d="M 200 95 Q 230 160 240 210" fill="none" stroke="#fcf8f2" strokeWidth="18" strokeLinecap="round" />
                  <path d="M 125 210 L 115 360" fill="none" stroke="#d1c4b2" strokeWidth="30" strokeLinecap="round" />
                  <path d="M 125 210 L 115 360" fill="none" stroke="#fcf8f2" strokeWidth="24" strokeLinecap="round" />
                  <path d="M 175 210 L 185 360" fill="none" stroke="#d1c4b2" strokeWidth="30" strokeLinecap="round" />
                  <path d="M 175 210 L 185 360" fill="none" stroke="#fcf8f2" strokeWidth="24" strokeLinecap="round" />
                  {/* Pectoral lines */}
                  <path d="M 120 120 Q 150 135 180 120" fill="none" stroke="#d1c4b2" strokeWidth="1.5" />
                  <circle cx="150" cy="180" r="2.5" fill="#d1c4b2" />
                </>
              )}

              {currentView === "body-back" && (
                <>
                  <circle cx="150" cy="50" r="25" fill="#fcf8f2" stroke="#d1c4b2" strokeWidth="3" />
                  <rect x="140" y="75" width="20" height="20" fill="#fcf8f2" stroke="#d1c4b2" strokeWidth="3" />
                  <path d="M 100 95 L 200 95 Q 210 160 185 210 L 115 210 Q 90 160 100 95" fill="#fcf8f2" stroke="#d1c4b2" strokeWidth="3" />
                  <path d="M 100 95 Q 70 160 60 210" fill="none" stroke="#d1c4b2" strokeWidth="24" strokeLinecap="round" />
                  <path d="M 100 95 Q 70 160 60 210" fill="none" stroke="#fcf8f2" strokeWidth="18" strokeLinecap="round" />
                  <path d="M 200 95 Q 230 160 240 210" fill="none" stroke="#d1c4b2" strokeWidth="24" strokeLinecap="round" />
                  <path d="M 200 95 Q 230 160 240 210" fill="none" stroke="#fcf8f2" strokeWidth="18" strokeLinecap="round" />
                  <path d="M 125 210 L 115 360" fill="none" stroke="#d1c4b2" strokeWidth="30" strokeLinecap="round" />
                  <path d="M 125 210 L 115 360" fill="none" stroke="#fcf8f2" strokeWidth="24" strokeLinecap="round" />
                  <path d="M 175 210 L 185 360" fill="none" stroke="#d1c4b2" strokeWidth="30" strokeLinecap="round" />
                  <path d="M 175 210 L 185 360" fill="none" stroke="#fcf8f2" strokeWidth="24" strokeLinecap="round" />
                  {/* Back features */}
                  <path d="M 150 95 L 150 190" fill="none" stroke="#d1c4b2" strokeWidth="1.5" strokeDasharray="4,4" />
                  <path d="M 150 190 L 150 220" fill="none" stroke="#d1c4b2" strokeWidth="2" />
                  <path d="M 115 210 Q 150 230 185 210" fill="none" stroke="#d1c4b2" strokeWidth="2" />
                </>
              )}
            </svg>

            {/* Pins rendering */}
            {visiblePins.map(pin => (
              <div 
                key={pin.id}
                className={`absolute w-4 h-4 rounded-full border-2 transform -translate-x-1/2 -translate-y-1/2 cursor-pointer shadow-md ${getPinColor(pin.type)} hover:scale-125 transition-transform z-10`}
                style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                onClick={(e) => { e.stopPropagation(); setActivePinId(pin.id === activePinId ? null : pin.id); }}
              >
                {/* Active Pin Popup */}
                {activePinId === pin.id && (
                  <div className="absolute top-6 left-1/2 transform -translate-x-1/2 bg-white rounded shadow-xl border border-gray-200 p-3 w-48 z-50">
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-[10px] font-bold text-slate-500 uppercase">{pin.type}</span>
                      {!isRecordLocked && (
                        <button onClick={(e) => { e.stopPropagation(); handleDeletePin(pin.id); }} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-slate-800 font-medium whitespace-pre-wrap">{pin.notes}</p>
                  </div>
                )}
              </div>
            ))}

            {/* Pending Pin Modal */}
            {pendingCoords && (
              <div 
                className="absolute transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-2xl border border-slate-300 p-3 w-56 z-50"
                style={{ left: `${pendingCoords.x}%`, top: `${pendingCoords.y}%` }}
                onClick={e => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xs font-bold text-slate-700">Add Intervention</span>
                  <button onClick={() => setPendingCoords(null)} className="text-slate-400 hover:text-slate-600"><X className="w-3 h-3" /></button>
                </div>
                
                <select 
                  className="w-full mb-2 p-1.5 text-xs border border-gray-300 rounded outline-none focus:border-indigo-400"
                  value={newPinType}
                  onChange={(e) => setNewPinType(e.target.value as Pin["type"])}
                >
                  <option value="botox">Botox / Neurotoxin</option>
                  <option value="filler">Dermal Filler</option>
                  <option value="laser">Laser Spot</option>
                  <option value="surgery">Biopsy / Excision</option>
                  <option value="other">Other Injection / Treatment</option>
                </select>
                
                <textarea
                  className="w-full mb-2 p-1.5 text-xs border border-gray-300 rounded outline-none focus:border-indigo-400 resize-none h-16"
                  placeholder="e.g. 2 Units, 0.5ml Juvederm..."
                  value={newPinNotes}
                  onChange={(e) => setNewPinNotes(e.target.value)}
                  autoFocus
                />
                
                <button 
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold py-1.5 rounded transition"
                  onClick={handleSavePin}
                  disabled={!newPinNotes.trim()}
                >
                  Save Marker
                </button>
              </div>
            )}
            
          </div>
          <p className="text-[10px] text-slate-400 mt-2 italic text-center w-64">
            Click anywhere on the map to document injections, biopsies, or laser treatments.
          </p>
        </div>

        {/* Media & Photos Column */}
        <div className="flex flex-col">
           <p className="text-xs text-slate-500 mb-4 font-semibold uppercase tracking-wider flex items-center justify-between">
              Clinical Photo Gallery
              {!isRecordLocked && (
                <label className="cursor-pointer bg-indigo-50 hover:bg-indigo-100 text-indigo-600 px-2 py-1 rounded text-[10px] flex items-center gap-1 transition">
                  <Camera className="w-3 h-3" /> Add Photo
                  <input type="file" className="hidden" accept="image/*" onChange={handlePhotoUpload} />
                </label>
              )}
           </p>

           {photos.length === 0 ? (
              <div className="flex-1 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center p-6 text-center bg-slate-50/50 min-h-[300px]">
                <Camera className="w-10 h-10 text-slate-300 mb-3" />
                <p className="text-sm font-semibold text-slate-600">No photos uploaded</p>
                <p className="text-xs text-slate-400 mt-1">Upload before & after photos to track treatment progress.</p>
              </div>
           ) : (
              <div className="grid grid-cols-2 gap-3 overflow-y-auto max-h-[400px] pr-2">
                {photos.map(photo => (
                  <div key={photo.id} className="relative group rounded-lg overflow-hidden border border-slate-200 shadow-sm aspect-square bg-slate-100">
                    <img src={photo.url} alt="Clinical photo" className="w-full h-full object-cover" />
                    
                    <div className="absolute top-0 left-0 w-full bg-gradient-to-b from-black/60 to-transparent p-2">
                       <span className="text-[9px] font-bold text-white uppercase tracking-wider bg-black/40 px-1.5 py-0.5 rounded backdrop-blur-sm">
                         {photo.date}
                       </span>
                    </div>

                    {!isRecordLocked && (
                      <button 
                        onClick={() => { if (onPhotosChange) onPhotosChange(photos.filter(p => p.id !== photo.id)); }}
                        className="absolute bottom-2 right-2 bg-white/90 text-red-500 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white hover:text-red-600 shadow-sm"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
           )}
        </div>
      </div>
    </div>
  );
};

export default DermBodyMap;
